import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ChevronRight, ChevronLeft, MoveRight, X, Lock } from "lucide-react";
import axios from "utils/axios";
import PageLoader from "./PageLoader";
import { PiHouse } from "react-icons/pi";
import { IoIosLink } from "react-icons/io";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi2";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import Lightbox from "components/ui/Lightbox";
import SearchSelect from "components/ui/SearchSelect";
import { formatDate } from "utils/helperFunctions";
import DateRangePicker from "components/ui/DateRangePicker";

// ─── Constants ───
const ALL_STATUSES = [
  "overdue",
  "scheduled",
  "sent",
  "inProgress",
  "completed",
  "revisionSent",
  "revisionInProgress",
  "revisionCompleted",
  "approved",
];
const LOCKED_STATUSES = new Set(["overdue", "scheduled", "sent", "inProgress"]);
const MOVABLE_COLUMNS = new Set([
  "revisionSent",
  "revisionInProgress",
  "revisionCompleted",
  "approved",
]);
const CARDS_PER_PAGE = 20; // Show 20 cards initially, load more on click

const STATUS_BG = {
  overdue: "bg-red-50",
  scheduled: "bg-lime-50",
  sent: "bg-orange-50",
  inProgress: "bg-yellow-50",
  completed: "bg-blue-50",
  revisionSent: "bg-red-50",
  revisionInProgress: "bg-yellow-50",
  revisionCompleted: "bg-blue-50",
  approved: "bg-green-50",
};
const STATUS_COLOR = {
  overdue: "text-red-600",
  scheduled: "text-lime-600",
  sent: "text-orange-600",
  inProgress: "text-yellow-600",
  completed: "text-blue-600",
  revisionSent: "text-red-600",
  revisionInProgress: "text-yellow-600",
  revisionCompleted: "text-blue-600",
  approved: "text-green-600",
};

// ─── Memoized Card Content ───
const CardContent = memo(({ item, column }) => {
  const phaseDate =
    item.status === "revisionSent"
      ? item.phaseDates?.revisionSentAt
      : item.status === "revisionInProgress"
        ? item.phaseDates?.revisionInProgressAt
        : item.status === "revisionCompleted"
          ? item.phaseDates?.revisionCompletedAt
          : null;

  return (
    <>
      <p className="text-xs font-medium mb-2">
        {item.title} {" - "}
        <span className="text-xs text-darkergray">
          {formatDate(phaseDate || item.uploadedAt)}
        </span>
      </p>

      <span
        className={`${column.bgColor} ${column.color} text-xs px-2 py-0.5 rounded`}
      >
        {column.displayName}
      </span>
      <span className="text-xs px-2 py-0.5 rounded">{item.contractorName}</span>

      <div className="my-2 flex gap-2 items-center">
        <PiHouse className="w-4 h-4 text-darkergray" />
        <p className="text-xs">{item.address}</p>
      </div>

      {(item.marketingStatus === "draft" ||
        item.marketingStatus === "approved") && (
        <div className="my-2 flex items-center gap-2">
          <IoIosLink className="w-4 h-4 text-darkergray" />
          <div className="flex gap-4">
            <Link
              to={`https://www.beleef.com.au/marketing/draft/${item.propertyId}`}
              className="text-xs underline"
              target="_blank"
            >
              Draft Link
            </Link>
            {item.marketingStatus === "approved" && (
              <Link
                to={`https://www.beleef.com.au/marketing/approved/${item.propertyId}`}
                className="text-xs underline"
                target="_blank"
              >
                Approved Link
              </Link>
            )}
          </div>
        </div>
      )}

      <Link
        to={`/edits/${item.propertyId}`}
        className="text-xs underline"
        target="_blank"
      >
        Edit
      </Link>

      {item.launchToMarketDate && (
        <div className="my-2 flex gap-2 items-center">
          <HiOutlineSpeakerphone className="w-4 h-4 text-darkergray" />
          <p className="text-xs">
            Key Meeting: Launch to Market {formatDate(item.launchToMarketDate)}
          </p>
        </div>
      )}

      {item.comments?.length > 0 && <CommentsBox comments={item.comments} />}
    </>
  );
});

// ─── CommentsBox ───
const CommentsBox = memo(({ comments }) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const images = useMemo(
    () => comments.filter((c) => c.url).map((c) => ({ src: c.url })),
    [comments],
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Disclosure>
        {({ open }) => (
          <>
            <DisclosureButton className="rounded-lg w-full cursor-pointer bg-lightgray px-3 py-2 flex items-center justify-between">
              <span className="text-xs">Comments</span>
              <HiChevronDown
                className={`text-xs transition-transform duration-300 ${open ? "rotate-180" : ""}`}
              />
            </DisclosureButton>
            <DisclosurePanel className="py-2 text-sm text-start">
              <div className="w-full mt-2">
                {comments.map((comment, index) => (
                  <div
                    key={comment.id}
                    className="flex items-start gap-2 mb-4 text-xs"
                  >
                    <div className="w-6 h-6 rounded-full bg-darkergray flex items-center justify-center text-white font-semibold">
                      {comment.name[0]}
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-semibold">
                        {comment.name}
                      </span>
                      <span className="ml-2 text-darkergray text-xs">
                        {comment.createdAt
                          ? formatDistanceToNow(new Date(comment.createdAt), {
                              addSuffix: true,
                            })
                          : "N/A"}
                      </span>
                      <p className="text-xs mt-0.5 whitespace-pre-wrap">
                        {comment.text}
                      </p>
                    </div>
                    {comment.url && (
                      <img
                        src={comment.url}
                        alt="property"
                        className="rounded-md w-12 h-auto cursor-pointer"
                        onClick={() => setLightboxIndex(index)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>

      {images.length > 0 && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          isOpen={lightboxIndex >= 0}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
    </div>
  );
});

// ─── Kanban Column (memoized to prevent re-renders of other columns) ───
const KanbanColumn = memo(
  ({ columnId, column, visibleCount, onShowMore, onDragEndReady }) => {
    const isLocked = column.locked;
    const visibleItems = column.items.slice(0, visibleCount);
    const hasMore = column.items.length > visibleCount;

    return (
      <div className="flex-shrink-0 w-80 bg-lightergray rounded-lg">
        {/* Column header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <span
              className={`${column.bgColor} ${column.color} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2`}
            >
              {column.displayName}
              {isLocked && <Lock className="w-3 h-3" />}
            </span>
            <span className="text-sm text-darkgray">{column.items.length}</span>
          </div>
        </div>

        {/* Cards */}
        <Droppable droppableId={columnId} isDropDisabled={isLocked}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`p-2 space-y-3 min-h-[calc(100%-5rem)] ${isLocked ? "bg-lightergray" : snapshot.isDraggingOver ? "bg-lightergray" : ""}`}
            >
              {visibleItems.map((item, index) =>
                isLocked ? (
                  // Locked columns: plain div, no Draggable overhead
                  <div
                    key={item.id}
                    className="p-4 bg-white rounded-lg border shadow-sm cursor-default opacity-90"
                  >
                    <CardContent item={item} column={column} />
                  </div>
                ) : (
                  // Unlocked columns: full drag support
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`cursor-pointer hover:bg-lightergray p-4 bg-white rounded-lg border ${
                          snapshot.isDragging
                            ? "shadow-lg ring-2 ring-blue-500"
                            : "shadow-sm"
                        }`}
                      >
                        <CardContent item={item} column={column} />
                      </div>
                    )}
                  </Draggable>
                ),
              )}
              {provided.placeholder}

              {/* Show more button */}
              {hasMore && (
                <button
                  onClick={() => onShowMore(columnId)}
                  className="w-full py-2 text-xs font-medium text-darkergray hover:bg-white rounded-lg transition-colors"
                >
                  Show{" "}
                  {Math.min(CARDS_PER_PAGE, column.items.length - visibleCount)}{" "}
                  more ({column.items.length - visibleCount} remaining)
                </button>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  },
);

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
const ResponsiveKanban = () => {
  const [columns, setColumns] = useState({});
  const [activeColumn, setActiveColumn] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showExtrasOnly, setShowExtrasOnly] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Track how many cards are visible per column (lazy loading)
  const [visibleCounts, setVisibleCounts] = useState({});

  const handleShowMore = useCallback((columnId) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [columnId]: (prev[columnId] || CARDS_PER_PAGE) + CARDS_PER_PAGE,
    }));
  }, []);

  // Reset visible counts when filters change
  useEffect(() => {
    setVisibleCounts({});
  }, [selectedProperty, selectedContractor, showExtrasOnly, dateRange]);

  // ── Fetch data ──
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/admin/prepareMarketing");
        const { data } = response.data;

        const transformedColumns = {};
        for (const status of ALL_STATUSES) {
          const displayName = status
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase());
          transformedColumns[status] = {
            name: status,
            displayName,
            bgColor: STATUS_BG[status] || "bg-lightergray",
            color: STATUS_COLOR[status] || "text-darkergray",
            locked: LOCKED_STATUSES.has(status),
            items: [],
          };
        }

        // Single pass to bucket items
        for (const item of data) {
          const col = transformedColumns[item.status];
          if (col) {
            col.items.push({
              ...item,
              id: `${item.eventId}-${col.items.length}`,
              title: item.summary,
            });
          }
        }

        setColumns(transformedColumns);
        setActiveColumn(ALL_STATUSES[0]);
      } catch (error) {
        console.error("Error fetching kanban data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const response = await axios.get(
          "/admin/contractor?profileComplete=true",
        );
        if (response.data.success) setContractors(response.data.data);
      } catch (error) {
        console.error("Error fetching contractors", error);
      }
    };
    const fetchProperties = async () => {
      try {
        const response = await axios.get("/admin/events/uniqueProperties");
        if (response.data.success) setProperties(response.data.data);
      } catch (error) {
        console.error("Error fetching properties", error);
      }
    };
    fetchContractors();
    fetchProperties();
  }, []);

  // ── Filter options ──
  const propertyOptions = useMemo(
    () => [
      { label: "All Properties", value: "all" },
      ...properties.map((p) => ({
        label: p.address,
        value: p.propertyId || p._id || p.address,
      })),
    ],
    [properties],
  );
  const contractorOptions = useMemo(
    () => [
      { label: "All Contractors", value: "all" },
      ...contractors.map((c) => ({ label: c.name, value: c._id })),
    ],
    [contractors],
  );

  // ── Navigation ──
  const handlePrevColumn = useCallback(() => {
    setActiveColumn((prev) => {
      const idx = ALL_STATUSES.indexOf(prev);
      return idx > 0 ? ALL_STATUSES[idx - 1] : prev;
    });
  }, []);

  const handleNextColumn = useCallback(() => {
    setActiveColumn((prev) => {
      const idx = ALL_STATUSES.indexOf(prev);
      return idx < ALL_STATUSES.length - 1 ? ALL_STATUSES[idx + 1] : prev;
    });
  }, []);

  // ── Move logic ──
  const canMove = useCallback((sourceId, destId) => {
    if (sourceId === destId) return false;
    if (LOCKED_STATUSES.has(sourceId) || LOCKED_STATUSES.has(destId))
      return false;
    if (sourceId === "completed" && !MOVABLE_COLUMNS.has(destId)) return false;
    if (MOVABLE_COLUMNS.has(sourceId) && destId === "completed") return false;
    if (!MOVABLE_COLUMNS.has(sourceId) && sourceId !== "completed")
      return false;
    return true;
  }, []);

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const { source, destination } = result;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      )
        return;
      if (!canMove(source.droppableId, destination.droppableId)) return;

      let movedItem = null;
      setColumns((prev) => {
        const srcCol = prev[source.droppableId];
        const dstCol = prev[destination.droppableId];
        const srcItems = [...srcCol.items];
        const dstItems = [...dstCol.items];
        [movedItem] = srcItems.splice(source.index, 1);
        dstItems.splice(destination.index, 0, movedItem);
        return {
          ...prev,
          [source.droppableId]: { ...srcCol, items: srcItems },
          [destination.droppableId]: { ...dstCol, items: dstItems },
        };
      });

      // Fire and forget API call
      if (movedItem) {
        axios
          .post("/prepareMarketing/status", {
            eventId: movedItem.eventId,
            address: movedItem.address,
            propertyId: movedItem.propertyId,
            status: destination.droppableId,
          })
          .catch((err) => console.error("Error updating status:", err));
      }
    },
    [canMove],
  );

  const moveCard = useCallback(
    (cardId, sourceColId, targetColId) => {
      if (!canMove(sourceColId, targetColId)) return;

      let cardToMove = null;
      setColumns((prev) => {
        const srcCol = prev[sourceColId];
        const dstCol = prev[targetColId];
        cardToMove = srcCol.items.find((item) => item.id === cardId);
        if (!cardToMove) return prev;
        return {
          ...prev,
          [sourceColId]: {
            ...srcCol,
            items: srcCol.items.filter((i) => i.id !== cardId),
          },
          [targetColId]: { ...dstCol, items: [...dstCol.items, cardToMove] },
        };
      });

      if (cardToMove) {
        axios
          .post("/prepareMarketing/status", {
            eventId: cardToMove.eventId,
            address: cardToMove.address,
            propertyId: cardToMove.propertyId,
            status: targetColId,
          })
          .catch((err) => console.error("Error updating status:", err));
      }
    },
    [canMove],
  );

  // ── Filtered + sorted columns (heavy computation, memoized) ──
  const filteredColumns = useMemo(() => {
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to + "T23:59:59") : null;

    const result = {};
    for (const [columnId, column] of Object.entries(columns)) {
      const filtered = column.items.filter((item) => {
        const itemDate = item.uploadedAt ? new Date(item.uploadedAt) : null;
        return (
          (!selectedProperty ||
            selectedProperty.value === "all" ||
            item.address === selectedProperty.label) &&
          (!selectedContractor ||
            selectedContractor.value === "all" ||
            item.contractorName === selectedContractor.label) &&
          (!showExtrasOnly || item.summary.toLowerCase().includes("extras")) &&
          (!fromDate || !itemDate || itemDate >= fromDate) &&
          (!toDate || !itemDate || itemDate <= toDate)
        );
      });

      filtered.sort(
        (a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0),
      );

      result[columnId] = { ...column, items: filtered };
    }
    return result;
  }, [
    columns,
    selectedProperty,
    selectedContractor,
    showExtrasOnly,
    dateRange,
  ]);

  if (loading) return <PageLoader />;

  const currentColumnIndex = ALL_STATUSES.indexOf(activeColumn);
  const currentColumn = filteredColumns[activeColumn];

  return (
    <div className="space-y-2">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <div className="w-full max-w-sm">
          <SearchSelect
            options={propertyOptions}
            value={selectedProperty}
            onChange={setSelectedProperty}
            placeholder="Select a property..."
            isClearable
          />
        </div>
        <div className="w-full max-w-sm">
          <SearchSelect
            options={contractorOptions}
            value={selectedContractor}
            onChange={setSelectedContractor}
            placeholder="Select a contractor..."
            isClearable
          />
        </div>
        <DateRangePicker onChange={(range) => setDateRange(range)} />
      </div>

      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={showExtrasOnly}
          onChange={() => setShowExtrasOnly((prev) => !prev)}
          className="form-checkbox"
        />
        <span>Show only items with "extras"</span>
      </label>

      <div className="w-full h-[75vh] relative bg-lightergray">
        {/* Desktop View */}
        <div className="hidden md:block h-full">
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-4 p-4 h-full overflow-x-auto">
              {Object.entries(filteredColumns).map(([columnId, column]) => (
                <KanbanColumn
                  key={columnId}
                  columnId={columnId}
                  column={column}
                  visibleCount={visibleCounts[columnId] || CARDS_PER_PAGE}
                  onShowMore={handleShowMore}
                />
              ))}
            </div>
          </DragDropContext>
        </div>

        {/* Mobile View */}
        <div className="md:hidden h-full flex flex-col">
          <div className="flex items-center justify-between p-4 bg-white border-b">
            <button
              onClick={handlePrevColumn}
              disabled={currentColumnIndex === 0}
              className="p-2 rounded-full hover:bg-lightergray disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <span
                className={`${currentColumn?.bgColor} ${currentColumn?.color} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2`}
              >
                {currentColumn?.displayName}
                {currentColumn?.locked && <Lock className="w-3 h-3" />}
              </span>
              <span className="text-sm text-darkgray">
                {currentColumnIndex + 1} of {ALL_STATUSES.length}
              </span>
            </div>
            <button
              onClick={handleNextColumn}
              disabled={currentColumnIndex === ALL_STATUSES.length - 1}
              className="p-2 rounded-full hover:bg-lightergray disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {currentColumn?.items.map((item) => (
              <div
                key={item.id}
                className={`bg-white p-4 rounded-lg border shadow-sm space-y-2 ${currentColumn.locked ? "opacity-90" : ""}`}
                onClick={() => {
                  if (!currentColumn.locked) {
                    setSelectedCard({ ...item, columnId: activeColumn });
                    setShowMoveDialog(true);
                  }
                }}
              >
                <CardContent item={item} column={currentColumn} />
                <div className="flex items-center space-x-2 mt-2">
                  {!currentColumn.locked && (
                    <MoveRight className="w-4 h-4 text-darkgray" />
                  )}
                  {currentColumn.locked && (
                    <Lock className="w-3 h-3 text-darkgray" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {showMoveDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center p-4">
              <div className="bg-white rounded-t-lg md:rounded-lg w-full max-w-sm">
                <div className="p-4 border-b flex justify-between items-center">
                  <h4>Move Card</h4>
                  <button
                    onClick={() => setShowMoveDialog(false)}
                    className="p-1 rounded-full hover:bg-lightergray"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 space-y-2">
                  {Object.entries(columns).map(
                    ([columnId, column]) =>
                      !column.locked &&
                      columnId !== selectedCard?.columnId && (
                        <button
                          key={columnId}
                          onClick={() => {
                            moveCard(
                              selectedCard.id,
                              selectedCard.columnId,
                              columnId,
                            );
                            setShowMoveDialog(false);
                          }}
                          className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-lightergray"
                        >
                          <div className="flex items-center space-x-3">
                            <span
                              className={`${column.bgColor} ${column.color} w-2 h-2 rounded-full`}
                            />
                            <span className="text-sm font-medium">
                              {column.displayName}
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-darkgray" />
                        </button>
                      ),
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveKanban;
